import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  message: string;
}

/**
 * Top-level fault isolation for the whole SPA. A render-time throw in ANY page
 * would otherwise unmount to a blank white screen with no recovery. This catches
 * it and shows a branded fallback with reload / go-home actions instead.
 *
 * Scoped boundaries (e.g. QRGenerationErrorBoundary) still handle their own
 * domains; this is the last line of defense for everything else.
 */
class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, message: error?.message || 'Something went wrong.' };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Surface for diagnostics without leaking to the user.
    console.error('AppErrorBoundary caught a render error:', error, errorInfo?.componentStack);

    // Backstop for a STALE-BUILD chunk 404 that didn't go through lazyWithRetry
    // (e.g. a chunk requested by an already-mounted component after a deploy).
    // Auto-reload ONCE to pick up the fresh build instead of stranding the user on
    // the fallback. 15s guard shared with lazyWithRetry prevents a reload loop.
    if (AppErrorBoundary.isChunkLoadError(error)) {
      try {
        const KEY = 'vmt-chunk-reload-at';
        const last = Number(window.sessionStorage.getItem(KEY) || '0');
        if (Date.now() - last > 15000) {
          window.sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        /* sessionStorage unavailable — leave the manual reload fallback in place */
      }
    }
  }

  private static isChunkLoadError(error: Error): boolean {
    const name = error?.name || '';
    const msg = error?.message || '';
    return name === 'ChunkLoadError' ||
      /loading chunk [\w-]+ failed/i.test(msg) ||
      /failed to (fetch dynamically imported module|import)/i.test(msg) ||
      /importing a module script failed/i.test(msg);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleHome = (): void => {
    // Full navigation resets React state so a poisoned page can't re-crash.
    window.location.assign('/');
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '2rem',
          gap: '1rem',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        }}
      >
        <AlertTriangle size={48} color="#d32f2f" aria-hidden="true" />
        <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1a1a1a' }}>
          Something went wrong
        </h1>
        <p style={{ margin: 0, maxWidth: 520, color: '#555', lineHeight: 1.5 }}>
          We hit an unexpected error loading this page. Your data is safe — please
          reload, or head back to the home page.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={this.handleReload}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.65rem 1.25rem',
              background: '#d32f2f',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={18} aria-hidden="true" /> Reload page
          </button>
          <button
            onClick={this.handleHome}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.65rem 1.25rem',
              background: '#fff',
              color: '#1a1a1a',
              border: '1px solid #ccc',
              borderRadius: 8,
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            <Home size={18} aria-hidden="true" /> Go home
          </button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
